/**
 * API Service for communicating with the backend
 */

import type {
  ApiResponse,
  Media,
  Verification,
  PaginatedResponse,
  AdminStats,
  AdminUser,
  LoginCredentials,
  AuthResponse,
  UploadResponse,
  VerificationDetail,
} from '../types';

// Base API URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('admin_token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: 'include',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    };
  }
}

/**
 * Authentication API
 */
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> => {
    const response = await apiRequest<AuthResponse>('/admin/login', {
      method: 'POST',
      body: credentials,
    });
    
    if (response.success && response.data?.token) {
      localStorage.setItem('admin_token', response.data.token);
    }
    
    return response;
  },

  logout: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiRequest<{ message: string }>('/admin/logout', {
      method: 'POST',
    });
    
    localStorage.removeItem('admin_token');
    return response;
  },

  getMe: async (): Promise<ApiResponse<AdminUser>> => {
    return apiRequest<AdminUser>('/admin/me');
  },
};

/**
 * Media/Images API
 */
export const mediaApi = {
  list: async (
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse<PaginatedResponse<Media>>> => {
    return apiRequest<PaginatedResponse<Media>>(
      `/admin/images?page=${page}&limit=${limit}`
    );
  },

  get: async (id: string): Promise<ApiResponse<Media>> => {
    return apiRequest<Media>(`/admin/images/${id}`);
  },

  upload: async (file: File, originalName: string): Promise<ApiResponse<UploadResponse>> => {
    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalName', originalName);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/images`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        credentials: 'include',
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UPLOAD_ERROR',
          message: error instanceof Error ? error.message : 'Upload failed',
        },
      };
    }
  },

  delete: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest<{ message: string }>(`/admin/images/${id}`, {
      method: 'DELETE',
    });
  },

  getShareUrl: async (id: string): Promise<ApiResponse<{ url: string }>> => {
    return apiRequest<{ url: string }>(`/admin/images/${id}/share`);
  },
};

/**
 * Verifications API
 */
export const verificationApi = {
  list: async (
    page: number = 1,
    limit: number = 20,
    mediaId?: string,
    status?: string
  ): Promise<ApiResponse<PaginatedResponse<Verification>>> => {
    let url = `/admin/verifications?page=${page}&limit=${limit}`;
    if (mediaId) url += `&mediaId=${mediaId}`;
    if (status) url += `&status=${status}`;
    return apiRequest<PaginatedResponse<Verification>>(url);
  },

  get: async (id: string): Promise<ApiResponse<VerificationDetail>> => {
    return apiRequest<VerificationDetail>(`/admin/verifications/${id}`);
  },

  getByMedia: async (
    mediaId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse<PaginatedResponse<VerificationDetail>>> => {
    return apiRequest<PaginatedResponse<VerificationDetail>>(
      `/admin/images/${mediaId}/verifications?page=${page}&limit=${limit}`
    );
  },
};

/**
 * Dashboard/Stats API
 */
export const statsApi = {
  get: async (): Promise<ApiResponse<AdminStats>> => {
    return apiRequest<AdminStats>('/admin/stats');
  },
};

/**
 * Settings API
 */
export const settingsApi = {
  updatePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest<{ message: string }>('/admin/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  updateVpnApiKey: async (apiKey: string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest<{ message: string }>('/admin/settings/vpn-api-key', {
      method: 'POST',
      body: { apiKey },
    });
  },
};

export default {
  auth: authApi,
  media: mediaApi,
  verification: verificationApi,
  stats: statsApi,
  settings: settingsApi,
};
